import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import SectionCTA from "@/components/SectionCTA";
import { SERVICES, EXTRA_SERVICES, IMG } from "@/data/site";

const PROCESS = [
  { title: "Consultation", time: "Day 1", text: "A conversation about how you live, what you love and what the room needs to do. No pressure, no jargon.", link: ["/booking", "Book a consultation"] },
  { title: "Design in The Studio", time: "Days 1–3", text: "Shape your room in 3D, lay real tiles at true scale and place every fixture — or let us do it with you.", link: ["/studio", "Open The Studio"] },
  { title: "Survey & fixed quote", time: "Week 1", text: "Vincent measures, checks walls, floors, plumbing and drainage, then prepares a clear itemised quote.", link: ["/quote", "Request a quote"] },
  { title: "Materials sourced", time: "Week 2", text: "Tiles, adhesives, fixtures and fittings ordered to the exact quantities from your design, with cutting allowance." },
  { title: "Preparation", time: "Install week", text: "Strip-out, boarding, levelling and full tanking in wet zones — the unseen work that makes a finish last." },
  { title: "Installation", time: "Install week", text: "Precision setting-out, tiling and fitting. Lines are planned so cuts land where the eye won’t." },
  { title: "Finishing & handover", time: "Completion", text: "Grouting, sealing, silicone and a full clean. We walk the room with you before we call it finished." },
];

const Step = ({ s, i }) => (
  <Reveal delay={i * 0.04}>
    <div className="relative grid md:grid-cols-2 gap-6 md:gap-16" data-testid={`process-step-${i}`}>
      <span className="absolute left-0 md:left-1/2 top-2 -translate-x-1/2 h-4 w-4 rounded-full bg-rossi-gold ring-8 ring-rossi-bg" />
      <div className={`pl-8 md:pl-0 ${i % 2 ? "md:order-2 md:pl-16" : "md:text-right md:pr-16"}`}>
        <p className="text-xs font-mono uppercase tracking-widest text-rossi-gold">{String(i + 1).padStart(2, "0")} · {s.time}</p>
        <h3 className="font-serif text-2xl sm:text-3xl text-rossi-cream mt-2">{s.title}</h3>
      </div>
      <div className={`pl-8 md:pl-0 ${i % 2 ? "md:order-1 md:text-right md:pr-16" : "md:pl-16"}`}>
        <p className="text-rossi-cream/70 leading-relaxed">{s.text}</p>
        {s.link && <Link to={s.link[0]} className="inline-flex items-center gap-1 text-sm text-rossi-gold mt-3 hover:gap-2 transition-all" data-testid={`process-link-${i}`}>{s.link[1]} <ArrowRight size={14} /></Link>}
      </div>
    </div>
  </Reveal>
);

const ServiceRow = ({ s, i }) => (
  <Link to={`/services/${s.slug}`} className="group grid grid-cols-[48px_1fr_auto] sm:grid-cols-[80px_1fr_220px_auto] items-center gap-4 sm:gap-8 py-7 border-b border-white/10 hover:border-rossi-gold/40 transition-colors" data-testid={`services-card-${s.slug}`}>
    <span className="font-mono text-sm text-rossi-stone">{String(i + 1).padStart(2, "0")}</span>
    <div>
      <h3 className="font-serif text-2xl sm:text-4xl text-rossi-cream group-hover:text-rossi-gold transition-colors">{s.title}</h3>
      <p className="text-sm text-rossi-cream/65 mt-1 max-w-xl">{s.short}</p>
    </div>
    <img src={s.hero} alt={`${s.title} by Rossi Ceramics`} className="hidden sm:block h-28 w-full object-cover rounded-xl opacity-80 group-hover:opacity-100 transition-opacity" />
    <ArrowUpRight className="text-rossi-gold group-hover:rotate-45 transition-transform" size={22} />
  </Link>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo title="Tiling & Bathroom Services" description="Wall & floor tiling, bathroom and wet room installation, kitchen splashbacks, feature walls and handyman services across Stockport and the North West." />
      <PageHero eyebrow="Services" title="From first idea to final grout line" subtitle="Every Rossi project follows the same careful path — Italian materials, British craftsmanship, one team from start to finish." image={IMG.bath[1]} />

      <section className="container-rossi py-20" data-testid="process-timeline">
        <Reveal><p className="eyebrow mb-3">How a Rossi project unfolds</p><h2 className="font-serif text-3xl sm:text-4xl text-rossi-cream mb-16 max-w-2xl">Seven steps. One team. No surprises.</h2></Reveal>
        <div className="relative space-y-16">
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-rossi-gold/60 via-rossi-gold/25 to-transparent" />
          {PROCESS.map((s, i) => <Step key={s.title} s={s} i={i} />)}
        </div>
      </section>

      <section className="container-rossi pb-20" data-testid="services-index">
        <Reveal><p className="eyebrow mb-3">What we do</p></Reveal>
        <div className="border-t border-white/10">{SERVICES.map((s, i) => <ServiceRow key={s.slug} s={s} i={i} />)}</div>
        <div className="flex flex-wrap gap-x-8 gap-y-3 mt-10 text-sm">
          <span className="text-rossi-stone">Also:</span>
          {EXTRA_SERVICES.map((e) => (e.to ? <Link key={e.title} to={e.to} className="text-rossi-cream/80 hover:text-rossi-gold">{e.title}</Link> : <span key={e.title} className="text-rossi-cream/80">{e.title}</span>))}
        </div>
      </section>

      <SectionCTA />
    </div>
  );
}
